import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import PrinterSelector from './components/PrinterSelector';
import FilamentSelector from './components/FilamentSelector';
import PrintSettings from './components/PrintSettings';
import CostBreakdown from './components/CostBreakdown';
import CostComparison from './components/CostComparison';
import PrintPreview from './components/PrintPreview';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const CostCalculator = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [selectedFilament, setSelectedFilament] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [activeTab, setActiveTab] = useState('calculator');

  const [printSettings, setPrintSettings] = useState({
    duration: '',
    weight: '',
    infill: 20,
    speed: 50,
    layerHeight: 0.2,
    supportMaterial: false,
    quantity: 1
  });

  const [costs, setCosts] = useState({
    material: 0,
    electricity: 0,
    depreciation: 0,
    labor: 0,
    total: 0,
    perUnit: 0,
    estimatedTime: 0,
    materialUsed: 0
  });

  // Mock data for printers
  const mockPrinters = [
    {
      id: "printer-1",
      name: "Ender 3 V2",
      brand: "Creality",
      model: "Ender 3 V2",
      hourlyRate: "15,50",
      powerConsumption: 220
    },
    {
      id: "printer-2", 
      name: "Prusa i3 MK3S+",
      brand: "Prusa Research",
      model: "i3 MK3S+",
      hourlyRate: "22,00",
      powerConsumption: 120
    },
    {
      id: "printer-3",
      name: "Bambu Lab X1 Carbon",
      brand: "Bambu Lab",
      model: "X1 Carbon",
      hourlyRate: "35,00",
      powerConsumption: 350
    }
  ];

  // Mock data for filaments
  const mockFilaments = [
    {
      id: "fil-1",
      brand: "SUNLU",
      material: "PLA",
      color: "Branco",
      colorHex: "#ffffff",
      pricePerKg: "89,90",
      stock: 850,
      density: 1.24
    },
    {
      id: "fil-2",
      brand: "eSUN",
      material: "ABS",
      color: "Preto",
      colorHex: "#000000", 
      pricePerKg: "95,50",
      stock: 650,
      density: 1.05
    },
    {
      id: "fil-3",
      brand: "Polymaker",
      material: "PETG",
      color: "Azul",
      colorHex: "#2563eb",
      pricePerKg: "125,00",
      stock: 400,
      density: 1.27
    },
    {
      id: "fil-4",
      brand: "SUNLU",
      material: "PLA",
      color: "Vermelho",
      colorHex: "#dc2626",
      pricePerKg: "89,90", 
      stock: 750,
      density: 1.24
    }
  ];

  // Mock comparison data
  const mockComparisonData = [
    { material: 'PLA', cost: 25.50 },
    { material: 'ABS', cost: 32.80 },
    { material: 'PETG', cost: 41.25 },
    { material: 'TPU', cost: 55.90 }
  ];

  // Mock historical data
  const mockHistoricalData = [
    { month: 'Jan', avgCost: 28.50, materialCost: 22.30 },
    { month: 'Fev', avgCost: 31.20, materialCost: 24.80 },
    { month: 'Mar', avgCost: 29.80, materialCost: 23.50 },
    { month: 'Abr', avgCost: 33.10, materialCost: 26.20 },
    { month: 'Mai', avgCost: 35.40, materialCost: 28.90 },
    { month: 'Jun', avgCost: 32.60, materialCost: 25.70 }
  ];

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSettingsChange = (field, value) => {
    setPrintSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSliderChange = (field, value) => {
    setPrintSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateCosts = () => {
    if (!selectedPrinter || !selectedFilament || !printSettings?.duration || !printSettings?.weight) {
      return;
    }

    setIsCalculating(true);

    // Simulate calculation delay
    setTimeout(() => {
      const printer = mockPrinters?.find(p => p?.id === selectedPrinter);
      const filament = mockFilaments?.find(f => f?.id === selectedFilament);
      
      const duration = parseFloat(printSettings?.duration);
      const weight = parseFloat(printSettings?.weight);
      const quantity = parseInt(printSettings?.quantity) || 1;

      // Calculate individual costs
      const materialCost = (weight / 1000) * parseFloat(filament?.pricePerKg?.replace(',', '.')) * quantity;
      const electricityCost = (duration * printer?.powerConsumption / 1000) * 0.65 * quantity; // R$ 0.65 per kWh
      const depreciationCost = duration * parseFloat(printer?.hourlyRate?.replace(',', '.')) * 0.1 * quantity;
      const laborCost = duration * parseFloat(printer?.hourlyRate?.replace(',', '.')) * quantity;

      // Apply support material multiplier
      const supportMultiplier = printSettings?.supportMaterial ? 1.2 : 1;
      const adjustedMaterialCost = materialCost * supportMultiplier;

      const totalCost = adjustedMaterialCost + electricityCost + depreciationCost + laborCost;

      setCosts({
        material: adjustedMaterialCost,
        electricity: electricityCost,
        depreciation: depreciationCost,
        labor: laborCost,
        total: totalCost,
        perUnit: totalCost / quantity,
        estimatedTime: duration * quantity,
        materialUsed: weight * quantity * supportMultiplier
      });

      setIsCalculating(false);
    }, 1500);
  };

  // Auto-calculate when parameters change
  useEffect(() => {
    if (selectedPrinter && selectedFilament && printSettings?.duration && printSettings?.weight) {
      calculateCosts();
    }
  }, [selectedPrinter, selectedFilament, printSettings]);

  const isFormValid = selectedPrinter && selectedFilament && printSettings?.duration && printSettings?.weight;

  const previewData = {
    totalCost: costs?.total,
    printTime: costs?.estimatedTime,
    materialNeeded: costs?.materialUsed,
    powerConsumption: (costs?.estimatedTime * (mockPrinters?.find(p => p?.id === selectedPrinter)?.powerConsumption || 0) / 1000)?.toFixed(2),
    layers: Math.ceil((100 / printSettings?.layerHeight) * (printSettings?.infill / 100)),
    hasEnoughStock: costs?.materialUsed <= (mockFilaments?.find(f => f?.id === selectedFilament)?.stock || 0),
    availableStock: mockFilaments?.find(f => f?.id === selectedFilament)?.stock || 0
  };

  const handleSaveCalculation = () => {
    // Mock save functionality
    alert('Cálculo salvo com sucesso!');
  };

  const handleStartPrint = () => {
    // Mock start print functionality
    alert('Impressão iniciada! Redirecionando para o dashboard...');
  };

  const tabs = [
    { id: 'calculator', label: 'Calculadora', icon: 'Calculator' },
    { id: 'comparison', label: 'Comparação', icon: 'BarChart3' },
    { id: 'preview', label: 'Preview', icon: 'Eye' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onMenuToggle={handleMobileMenuToggle}
        isMobileMenuOpen={isMobileMenuOpen}
      />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        onToggle={handleSidebarToggle}
        isMobileOpen={isMobileMenuOpen}
        onMobileClose={() => setIsMobileMenuOpen(false)}
      />
      <main className={`
        pt-16 transition-all duration-300 ease-in-out
        lg:pt-16 sm:pt-14
        ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
      `}>
        <div className="p-6 lg:p-6 sm:p-4">
          <Breadcrumb />
          
          {/* Page Header */}
          <motion.div 
            className="mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Calculator" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground lg:text-3xl sm:text-2xl">
                  Calculadora de Custos
                </h1>
                <p className="text-muted-foreground lg:text-base sm:text-sm">
                  Simule custos de impressão 3D com precisão
                </p>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-muted/30 p-1 rounded-lg w-fit">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-micro
                    ${activeTab === tab?.id 
                      ? 'bg-primary text-primary-foreground shadow-elevation-1' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Calculator Tab */}
          {activeTab === 'calculator' && (
            <motion.div 
              className="grid grid-cols-1 xl:grid-cols-3 gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Left Column - Configuration */}
              <div className="xl:col-span-2 space-y-6">
                <PrinterSelector 
                  selectedPrinter={selectedPrinter}
                  onPrinterChange={setSelectedPrinter}
                  printers={mockPrinters}
                />
                
                <FilamentSelector 
                  selectedFilament={selectedFilament}
                  onFilamentChange={setSelectedFilament}
                  filaments={mockFilaments}
                />
                
                <PrintSettings 
                  settings={printSettings}
                  onSettingsChange={handleSettingsChange}
                  onSliderChange={handleSliderChange}
                />
              </div>

              {/* Right Column - Results */}
              <div className="space-y-6">
                <CostBreakdown 
                  costs={costs}
                  isCalculating={isCalculating}
                />
              </div>
            </motion.div>
          )}

          {/* Comparison Tab */}
          {activeTab === 'comparison' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <CostComparison 
                comparisonData={mockComparisonData}
                historicalData={mockHistoricalData}
              />
            </motion.div>
          )}

          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <motion.div 
              className="max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <PrintPreview 
                previewData={previewData}
                onSaveCalculation={handleSaveCalculation}
                onStartPrint={handleStartPrint}
                isValid={isFormValid}
              />
            </motion.div>
          )}

          {/* Quick Actions - Always visible */}
          <motion.div 
            className="fixed bottom-6 right-6 flex flex-col space-y-3 z-50"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <Button
              variant="secondary"
              size="icon"
              className="w-12 h-12 rounded-full shadow-elevation-2"
              title="Limpar formulário"
              onClick={() => {
                setSelectedPrinter('');
                setSelectedFilament('');
                setPrintSettings({
                  duration: '',
                  weight: '',
                  infill: 20,
                  speed: 50,
                  layerHeight: 0.2,
                  supportMaterial: false,
                  quantity: 1
                });
              }}
            >
              <Icon name="RotateCcw" size={20} />
            </Button>

            <Button
              variant="primary"
              size="icon"
              className="w-12 h-12 rounded-full shadow-elevation-2"
              title="Recalcular"
              onClick={calculateCosts}
              disabled={!isFormValid}
            >
              <Icon name="RefreshCw" size={20} />
            </Button>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export default CostCalculator;